import type { ProfileCustomization } from '@/types'

interface AccessibilitySettingsProps {
  customization: ProfileCustomization
  onChange: (customization: ProfileCustomization) => void
}

export default function AccessibilitySettings({
  customization,
  onChange,
}: AccessibilitySettingsProps) {
  const handleToggle = (field: keyof ProfileCustomization, value: boolean) => {
    onChange({
      ...customization,
      [field]: value,
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Accessibility & Performance
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure settings for better accessibility and performance.
        </p>
      </div>

      <div className="space-y-3">
        {/* Animation Toggle */}
        <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex-1">
            <label
              htmlFor="animations-toggle"
              className="text-sm font-medium text-gray-900 cursor-pointer"
            >
              Enable Animations
            </label>
            <p className="text-xs text-gray-600 mt-1">
              Disable animations for reduced motion or better performance. Improves
              accessibility for users with motion sensitivity.
            </p>
          </div>
          <div className="ml-4">
            <button
              id="animations-toggle"
              role="switch"
              aria-checked={customization.animationsEnabled ?? true}
              onClick={() =>
                handleToggle(
                  'animationsEnabled',
                  !(customization.animationsEnabled ?? true)
                )
              }
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                ${
                  customization.animationsEnabled ?? true
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${
                    customization.animationsEnabled ?? true
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }
                `}
              />
            </button>
          </div>
        </div>

        {/* Scroll Navigation Toggle */}
        <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex-1">
            <label
              htmlFor="scroll-nav-toggle"
              className="text-sm font-medium text-gray-900 cursor-pointer"
            >
              Show Scroll Navigation
            </label>
            <p className="text-xs text-gray-600 mt-1">
              Display a floating navigation menu for quick section jumps on your
              profile.
            </p>
          </div>
          <div className="ml-4">
            <button
              id="scroll-nav-toggle"
              role="switch"
              aria-checked={customization.showScrollNav ?? false}
              onClick={() =>
                handleToggle('showScrollNav', !(customization.showScrollNav ?? false))
              }
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                ${
                  customization.showScrollNav ?? false
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${
                    customization.showScrollNav ?? false
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Tip:</strong> Disabling animations can improve page load performance on
          slower devices and is recommended for users who prefer reduced motion.
        </p>
      </div>
    </div>
  )
}
